/**
 * TCPA-style SMS consent capture/edit, rendered inside the shared
 * `<Lightbox>` modal (not a new modal primitive — see that component's own
 * doc comment). Two modes:
 *
 *   'gate'  Staff clicked Text on a tenant with no consent decision
 *           recorded yet — messaging-context.tsx opens this before ever
 *           opening the Surge conversation panel. Resolving Yes here is
 *           what lets the caller proceed to open it.
 *   'edit'  The tenant profile's standalone "Manage SMS consent" control
 *           (TenantForm.tsx), reachable at any time independent of texting.
 *
 * Self-sufficient: fetches its own current status on mount rather than
 * requiring the caller to pass it in. The disclosure comes back on that same
 * DTO, built server-side (apps/api lib/sms-disclosures.ts) — staff read it
 * to the tenant, and saving echoes it back so the record holds exactly the
 * text that was read. A tenant with no phone on file can't have consent
 * recorded: consent names the number it covers.
 */

import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { fetchSmsConsent, saveSmsConsent, type SmsConsentDto } from '../lib/sms-consent.js';
import { Field, FormErrors, TextArea } from './Form.js';
import { Lightbox } from './Lightbox.js';

interface SmsConsentDialogProps {
  tenantId: string;
  tenantName: string;
  mode: 'gate' | 'edit';
  onResolved: (dto: SmsConsentDto) => void;
  onClose: () => void;
}

export function SmsConsentDialog({
  tenantId,
  tenantName,
  mode,
  onResolved,
  onClose,
}: SmsConsentDialogProps) {
  const [current, setCurrent] = useState<SmsConsentDto | null>(null);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [status, setStatus] = useState<'CONSENTED' | 'DECLINED' | ''>('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [saveError, setSaveError] = useState<unknown>(null);

  useEffect(() => {
    let cancelled = false;
    void fetchSmsConsent(tenantId)
      .then((consent) => {
        if (cancelled) return;
        setCurrent(consent);
        if (consent.status === 'CONSENTED' || consent.status === 'DECLINED') {
          setStatus(consent.status);
        }
      })
      .catch((caught: unknown) => {
        if (!cancelled) setLoadError(caught);
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!status || !current?.disclosure) return;
    setBusy(true);
    setSaveError(null);
    try {
      const dto = await saveSmsConsent(tenantId, {
        status,
        reason,
        disclosure: current.disclosure,
      });
      onResolved(dto);
    } catch (caught) {
      setSaveError(caught);
    } finally {
      setBusy(false);
    }
  }

  const title =
    mode === 'gate' ? `SMS consent needed — ${tenantName}` : `SMS consent — ${tenantName}`;

  return (
    <Lightbox title={title} onClose={onClose}>
      {loadError ? (
        <FormErrors error={loadError} />
      ) : !current ? (
        <p className="muted">Loading…</p>
      ) : (
        <form className="stack" onSubmit={(event) => void submit(event)}>
          {mode === 'gate' ? (
            <p className="muted">
              This tenant hasn't been asked about SMS consent yet. Record their decision before
              starting a text conversation.
            </p>
          ) : null}

          {current.disclosure ? (
            <div className="stack">
              <p>
                Read this to the tenant before recording their decision. It is saved with the
                decision as the record of what they agreed to.
              </p>
              <p className="alert" style={{ whiteSpace: 'pre-line' }}>
                {current.disclosure}
              </p>
            </div>
          ) : (
            <p className="alert alert--error">
              Add a phone number to this tenant's profile before recording SMS consent — consent has
              to name the number it covers.
            </p>
          )}

          {current.status !== 'NOT_ASKED' ? (
            <p className="muted">
              Currently:{' '}
              <strong>{current.status === 'CONSENTED' ? 'Consented' : 'Declined'}</strong>
              {current.updatedByName ? ` — recorded by ${current.updatedByName}` : ''}
              {current.source === 'SURGE_WEBHOOK' ? ' (tenant replied via SMS)' : ''}
              {current.consentedPhone ? ` for ${current.consentedPhone}` : ''}
              {current.reason ? `: "${current.reason}"` : ''}
            </p>
          ) : null}

          <Field label="Does this tenant consent to receiving SMS?" name="status" required>
            <div className="row">
              <button
                type="button"
                className={status === 'CONSENTED' ? 'button' : 'button button--quiet'}
                onClick={() => setStatus('CONSENTED')}
              >
                Yes
              </button>
              <button
                type="button"
                className={status === 'DECLINED' ? 'button' : 'button button--quiet'}
                onClick={() => setStatus('DECLINED')}
              >
                No
              </button>
            </div>
          </Field>

          <Field
            label="Reason"
            name="reason"
            required
            hint="Recorded alongside who made this change and when."
          >
            <TextArea name="reason" value={reason} onChange={setReason} rows={2} />
          </Field>

          <FormErrors error={saveError} />

          <div className="row">
            <button
              type="submit"
              className="button"
              disabled={busy || !status || reason.trim() === '' || !current.disclosure}
            >
              {busy ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              className="button button--quiet"
              onClick={onClose}
              disabled={busy}
            >
              Cancel
            </button>
          </div>
        </form>
      )}
    </Lightbox>
  );
}
